import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Search, X } from 'lucide-react';
import { Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useNotificationActions } from '@/lib/notifications/useNotificationActions';
import { useNotificationsUnreadCounts } from '@/lib/endpoints';
import type { NotificationTab } from '@/lib/notifications/notification-types';
import { NotificationList } from './NotificationList';
import { NotificationTabs } from './NotificationTabs';

/** Active-tab unread → the "X New" pill. Capped at 99+. */
function pillCount(tab: NotificationTab, counts?: { all: number; markets: number; account: number }): number {
  if (!counts) return 0;
  return tab === 'MARKETS' ? counts.markets : tab === 'ACCOUNT' ? counts.account : counts.all;
}

function cap99(n: number): string {
  return n > 99 ? '99+' : String(n);
}

export interface NotificationFeedProps {
  /** Fired after a row opens its pop-up — the desktop popover closes itself here. */
  onItemClick?: () => void;
}

/**
 * The full notification surface shared by the lobby drawer and the account-page
 * section: header ("Notifications" + active-tab "X New" pill + "Mark All Read"),
 * a search bar, the All/Markets/Account tabs, and the grouped list. Tab + search
 * drive the server-side query; "Mark All Read" clears the active tab only.
 */
export function NotificationFeed({ onItemClick }: NotificationFeedProps) {
  const [tab, setTab] = useState<NotificationTab>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Date-range filter (ARC-111) — ISO date strings (YYYY-MM-DD) passed straight
  // through to the list query. Collapsed behind a calendar toggle to stay
  // unobtrusive per the search Figma.
  const [showDates, setShowDates] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const hasDates = Boolean(dateFrom || dateTo);
  const { data: counts } = useNotificationsUnreadCounts();
  const { markAllRead, isMarkingAll } = useNotificationActions();

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const unread = useMemo(() => pillCount(tab, counts), [tab, counts]);

  const onTabChange = (next: NotificationTab) => {
    setTab(next);
  };

  const onMarkAll = () => {
    markAllRead(tab);
  };

  return (
    <div className="flex w-full flex-col" data-testid="notification-feed">
      <header className="flex items-center justify-between gap-2 px-4 pt-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold text-primary">Notifications</h2>
          {unread > 0 && (
            <span data-testid="new-pill" className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent">
              {cap99(unread)} New
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onMarkAll}
          disabled={isMarkingAll || unread === 0}
          data-testid="mark-all-read"
          className="text-sm font-semibold text-accent underline underline-offset-2 transition-opacity hover:opacity-80 disabled:opacity-40"
        >
          Mark All Read
        </button>
      </header>

      <div className="flex items-center gap-2 px-4 pt-3">
        <div className="relative flex flex-1 items-center rounded-full border border-accent">
          <Search className="pointer-events-none absolute left-3 size-4 text-accent" aria-hidden />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="search here"
            aria-label="Search notifications"
            data-testid="notification-search"
            className="h-9 rounded-full border-0 bg-transparent pl-9 pr-9 focus-visible:ring-0"
          />
          {search && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearch('')}
              className="absolute right-3 text-muted hover:text-primary"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            // Closing the row clears the range too — otherwise a filter would
            // stay applied with its controls hidden and no way to clear it.
            setShowDates((open) => {
              if (open) {
                setDateFrom('');
                setDateTo('');
              }
              return !open;
            });
          }}
          aria-label="Filter by date"
          aria-pressed={showDates || hasDates}
          data-testid="notification-date-toggle"
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-full border transition-colors',
            showDates || hasDates ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted hover:text-primary',
          )}
        >
          <CalendarDays className="size-4" />
        </button>
      </div>

      {showDates && (
        <div className="flex items-center gap-2 px-4 pt-2 text-xs text-muted" data-testid="notification-date-range">
          <label className="flex flex-1 flex-col gap-1">
            <span className="font-semibold">From</span>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="From date"
              data-testid="notification-date-from"
              className="h-9 rounded-lg border border-border bg-surface px-2 text-sm text-primary focus-visible:border-accent focus-visible:outline-none"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span className="font-semibold">To</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="To date"
              data-testid="notification-date-to"
              className="h-9 rounded-lg border border-border bg-surface px-2 text-sm text-primary focus-visible:border-accent focus-visible:outline-none"
            />
          </label>
          {hasDates && (
            <button
              type="button"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              aria-label="Clear dates"
              data-testid="notification-date-clear"
              className="mt-5 flex size-9 shrink-0 items-center justify-center rounded-lg text-muted hover:text-primary"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      )}

      <div className={cn('px-4 pt-3')}>
        <NotificationTabs active={tab} onChange={onTabChange} />
      </div>

      <div className="mt-3 max-h-[60vh] overflow-y-auto">
        <NotificationList
          tab={tab}
          q={debouncedSearch}
          dateFrom={dateFrom || undefined}
          dateTo={dateTo || undefined}
          onItemClick={onItemClick}
        />
      </div>
    </div>
  );
}
